import * as z from "zod";
import { AddressTypeEnum } from "../business/address";

/** Role enum */
export const RoleEnum = z.enum(["user", "admin", "staff"]);

/** Base user schema */
export const UserSchema = z.object({
  id: z.string().uuid().optional(),
  first_name: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .nullable()
    .optional(),
  last_name: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .nullable()
    .optional(),
  email: z.string().email("Invalid email address").toLowerCase(),
  email_verified: z.date().nullable().optional(),
  phone: z.string().nullable().optional(),
  image: z.string().url("Must be a valid URL").nullable().optional(),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .optional(),
  date_of_birth: z.date().nullable().optional(),
  role: RoleEnum.default("user"),
  company_id: z.string().uuid().nullable().optional(),
  job_title: z.string().max(100).optional(),
  is_active: z.boolean().default(true),
  last_login: z.date().nullable().optional(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

/** Schema for creating a new user */
export const createUserSchema = UserSchema.omit({
  id: true,
  created: true,
  updated: true,
  email_verified: true,
  last_login: true,
  company_id: true,
});

/** Schema for updating an existing user */
export const UpdateUserSchema = UserSchema.partial().omit({
  id: true,
  created: true,
  updated: true,
  email_verified: true,
  last_login: true,
});

/** Authentication schemas */
export const LoginSchema = z.object({
  email: z.string().email({ message: "Invalid email format" }).toLowerCase(),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const RegisterSchema = z
  .object({
    email: z.string().email({ message: "Invalid email format" }).toLowerCase(),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirm_password: z.string(),
    first_name: z.string().optional().nullable(),
    last_name: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    date_of_birth: z.date().optional().nullable(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"],
  });

export const ForgotPasswordSchema = z.object({
  email: z.string().email({ message: "Invalid email format" }).toLowerCase(),
});

export const OTPFormSchema = z.object({
  pin: z.string().min(6).max(6, "PIN must be 6 digits"),
});

export const OTPVerifySchema = z.object({
  pin: z.string().min(6).max(6, "PIN must be 6 digits"),
  email: z.string().email({ message: "Invalid email format" }).toLowerCase(),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const ResendOTPSchema = z.object({
  email: z.string().email({ message: "Invalid email format" }).toLowerCase(),
});

export const NewPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirm_password: z.string(),
    token: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"],
  });

/** Session and token schemas */
export const PasswordResetTokenSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  email: z.string().email(),
  token: z.string(),
  expires: z.date(),
  is_used: z.boolean().default(false),
  created: z.date(),
});

export const VerificationTokenSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  email: z.string().email(),
  token: z.string(),
  expires: z.date(),
  is_used: z.boolean().default(false),
  created: z.date(),
});

// Account Deletion Token Schema
export const UserDeleteTokenSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  email: z.string().email(),
  token: z.string(),
  expires: z.date(),
  is_used: z.boolean().default(false),
  created: z.date(),
});

// Account Deletion Confirmation Form Schema
export const AccountDeletionConfirmSchema = z.object({
  token: z.string(),
  confirmation: z.literal("DELETE", {
    errorMap: () => ({ message: "Please type DELETE to confirm" }),
  }),
});

/** User address schema */
export const UserAddressSchema = z.object({
  id: z.string().uuid(),
  address_id: z.string().uuid(),
  user_id: z.string().uuid(),
  address_type: AddressTypeEnum.default("additional"),
  label: z.string().max(50).optional(),
  is_primary: z.boolean().default(false),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

// Type exports
export type Login = z.infer<typeof LoginSchema>;
export type Register = z.infer<typeof RegisterSchema>;
export type ForgotPassword = z.infer<typeof ForgotPasswordSchema>;
export type OTPForm = z.infer<typeof OTPFormSchema>;
export type OTPVerify = z.infer<typeof OTPVerifySchema>;
export type ResendOTP = z.infer<typeof ResendOTPSchema>;
export type NewPassword = z.infer<typeof NewPasswordSchema>;
export type User = z.infer<typeof UserSchema>;
export type UpdateUser = z.infer<typeof UpdateUserSchema>;
export type PasswordResetToken = z.infer<typeof PasswordResetTokenSchema>;
export type VerificationToken = z.infer<typeof VerificationTokenSchema>;
export type UserAddress = z.infer<typeof UserAddressSchema>;
export type UserDeleteToken = z.infer<typeof UserDeleteTokenSchema>;
export type AccountDeletionConfirm = z.infer<
  typeof AccountDeletionConfirmSchema
>;

export const UserSessionSchema = z.object({
  id: z.string().uuid().nullable().optional(),
  role: z.enum(["admin", "staff", "user"]).nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  emailVerified: z.union([z.date(), z.string(), z.null()]).optional(),
  phone: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
  companyName: z.string().nullable().optional(),
  companyImage: z.string().nullable().optional(),
  paymentTerms: z.string().nullable().optional(),
  stripeCustomerId: z.string().nullable().optional(),
  tierId: z.string().uuid().nullable().optional(),
  addressId: z.string().uuid().nullable().optional(),
  permissions: z
    .record(
      z.object({
        create: z.boolean(),
        read: z.boolean(),
        update: z.boolean(),
        delete: z.boolean(),
        fields: z.record(
          z.object({
            read: z.boolean(),
            write: z.boolean(),
          })
        ),
      })
    )
    .optional(),
});
